import { inject } from "@angular/core";
import { Observable, catchError, map, of } from "rxjs";
import { CookieService } from "ngx-cookie-service";
import {
    CanActivateFn,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    Router,
    UrlTree,
    CanActivateChildFn
} from "@angular/router";

import { UserService } from "../services/user.service";
import { LoggerService } from "../../shared/services/logger.service";
import { UserProfileResponse } from "../../shared/models/request/user-profile.response";
import { CONSTANTS, COOKIE, SPLUNK, getActiveAdGroups } from "../../modules/login/util/constants";
import { Constants } from "../../shared/constants/constants";
import { APP_CONFIG } from "../services/app-config.service";

export const authGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
): Observable<boolean | UrlTree> | boolean | UrlTree => {

    const userService = inject(UserService);
    const cookieService = inject(CookieService);
    const loggerService = inject(LoggerService);
    const router = inject(Router);

    const isUserNameAvailable: boolean = cookieService.check(COOKIE.DP_ADM_USER_NAME);
    const isSessionIdAvailable: boolean = cookieService.check(COOKIE.DP_ADM_SID);
    const isJwtTokenAvailable: boolean = cookieService.check(COOKIE.DP_ADM_TOKEN);
    const isUserIdAvailable: boolean = cookieService.check(COOKIE.DP_ADM_USER_ID);

    if (isUserNameAvailable && isSessionIdAvailable && isJwtTokenAvailable && isUserIdAvailable) {
        if (userService.getUserName) {
            return authenticateUser(route, state, userService, loggerService, router, cookieService);
        } else {
            try {
                // Restore Session ID
                const sid: string = atob(cookieService.get(COOKIE.DP_ADM_SID));
                userService.setSessionID = sid;

                // Restore JWT Token
                const token: string = atob(cookieService.get(COOKIE.DP_ADM_TOKEN));
                userService.setJwtToken = token;

                // Restore User Id
                const userId: string = atob(cookieService.get(COOKIE.DP_ADM_USER_ID));
                userService.setUserIdentifier = userId;

                // Restore User Name
                const userName: string = atob(cookieService.get(COOKIE.DP_ADM_USER_NAME));
                userService.setUserName = userName;
                const isAdLogin = localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';
                const request = { userName: userName, adlogin: isAdLogin ? 'true' : 'false' };

                // Restore AD user profile from localStorage on page refresh / new tab (Azure login only)
                if (isAdLogin) {
                    const raw = localStorage.getItem('AD_USER_PROFILE');
                    if (raw) {
                        try {
                            const adProfile = JSON.parse(raw);
                            if (adProfile.adGroups?.length)  { userService.setAdGroups  = adProfile.adGroups;  }
                            if (adProfile.firstName)         { userService.setFirstName  = adProfile.firstName; }
                            if (adProfile.lastName)          { userService.setLastName   = adProfile.lastName;  }
                            if (adProfile.email)             { userService.setEmail      = adProfile.email;     }
                        } catch (_) { /* ignore malformed value */ }
                    }
                }

                // Restore User data
                return userService.userProfileRequest(request)
                    .pipe(
                        map((data: UserProfileResponse) => {
                            if (data?.responseHeader?.transactionNotification?.status === CONSTANTS.SUCCESS) {
                                // Set user profile
                                userService.setUserProfile = data.responseBody;
                                // Call session timeout logic
                                userService.handleSessionTimeout();
                                // Set user as authenticated
                                userService.setAuthenticated = true;
                                userService.isAuntheticated$.next(true);

                                // Splunk Update
                                loggerService.logEvent(
                                    SPLUNK.USER_PROFILE_SUCCESS_EVENT_CD,
                                    SPLUNK.USER_PROFILE_SUCCESS_EVENT_DESC,
                                    SPLUNK.USER_PROFILE_SUCCESS_ID,
                                    '',
                                    {
                                        adminUserId: userService.getUserIdentifier,
                                        adminUserName: userService.getUserName
                                    },
                                    CONSTANTS.SUCCESS
                                );

                                return authenticateUser(route, state, userService, loggerService, router, cookieService);
                            } else {
                                return navigateToLogin(userService, router, loggerService, userName);
                            }
                        }), catchError(() => {
                            return of(navigateToLogin(userService, router, loggerService, userName));
                        })
                    );
            } catch (error) {
                return of(navigateToLogin(userService, router, loggerService, Constants.UNAUTHUSER));
            }
        }
    } else {
        const isBenefitLookup = !state.url.includes(CONSTANTS.HOME_URL) && state.url.includes(Constants.BENEFITS_ROUTE);
        const wasAzureLogin = localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';

        userService.logoutUser();

        if (isBenefitLookup) {
            // Set BENEFIT_LOOKUP_URL after logoutUser() so it is not immediately deleted,
            // and survives the external Azure SSO redirect to be read on the /inbound-sso callback.
            cookieService.set(COOKIE.BENEFIT_LOOKUP_URL, state.url, 1, '/', globalThis.location.hostname, true, 'Strict');
            window.location.href = APP_CONFIG.DPADMIN_AZURE_SSO_URL;
            return false;
        }
        
        return router.createUrlTree([wasAzureLogin ? 'landing' : 'login']);
    }
};

export const authGuardChild: CanActivateChildFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
) => authGuard(route, state);

const authenticateUser = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    userService: UserService,
    loggerService: LoggerService,
    router: Router,
    cookieService: CookieService
): boolean | UrlTree => {

    // If user is not authenticated, redirect to login
    if (!userService.isAuthenticated) {
        const wasAzureLogin = localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';
        userService.logoutUser();
        return router.createUrlTree([wasAzureLogin ? 'landing' : 'login']);
    }

    // Azure AD users: enforce per-route AD group access
    const isAzureLogin = localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';
    if (isAzureLogin && userService.getAdGroups.length > 0) {
        const adRequiredGroups: string[] | undefined = route.data['adGroupAccessIds'];
        // undefined = no AD restriction on this route → allow all authenticated AD users
        if (adRequiredGroups === undefined) return true;
        // empty array = explicitly blocked for all AD users
        if (adRequiredGroups.length === 0) return router.createUrlTree(['home']);
        // Filter to current environment's groups only — prevents NPE group holders
        // from passing checks in PROD and vice versa.
        const envGroupValues: string[] = Object.values(getActiveAdGroups());
        const envGroups = adRequiredGroups.filter(g => envGroupValues.includes(g));
        // non-empty: require at least one matching group from the current environment
        return envGroups.some(g => userService.getAdGroups.includes(g))
            ? true
            : router.createUrlTree(['home']);
    }

    const userAccessList = [userService.getUserRole, ...userService.getUserPrivilegesList];
    const requiredAccessList = route.data['requiredAccess'] || [];

    // If user does not have access to the page, redirect to home
    if (requiredAccessList.length && !requiredAccessList.some((access: string) => userAccessList.includes(access))) {

        // Update splunk on unauthorized url access
        loggerService.logEvent(
            SPLUNK.UNAUTH_URL_ACCESS_ATTEMPT_CD,
            SPLUNK.UNAUTH_URL_ACCESS_ATTEMPT_DESC,
            SPLUNK.UNAUTH_URL_ACCESS_ATTEMPT_ID,
            '',
            {
                adminUserName: userService.getUserName,
                adminUserId: userService.getUserIdentifier,
                auditMessageText: `${CONSTANTS.UNAUTHORIZED_AUDIT_TXT} ${state.url}`
            },
            CONSTANTS.UNAUTHORIZED_ACCESS
        );

        return router.createUrlTree(['home']);
    }

    return true;
}

const navigateToLogin = (
    userService: UserService,
    router: Router,
    loggerService: LoggerService,
    userName: string
): boolean | UrlTree => {
    loggerService.logEvent(
        SPLUNK.USER_PROFILE_FAILURE_EVENT_CD,
        SPLUNK.USER_PROFILE_FAILURE_EVENT_DESC,
        SPLUNK.USER_PROFILE_FAILURE_ID,
        '',
        {
            adminUserName: userName
        },
        CONSTANTS.FAILURE
    );
    const wasAzureLogin = localStorage.getItem('ENABLE_AZURE_LOGIN') === 'true';
    userService.logoutUser();
    return router.createUrlTree([wasAzureLogin ? 'landing' : 'login']);
}